/**
 * Stripe's embedded Connect screens (sign-up, payments, payouts, bank details)
 * for clubs whose account has no Stripe dashboard. Loads Stripe's script the
 * way @stripe/connect-js does, without adding the package to the workspace.
 */
import { useEffect, useRef, useState } from 'react';
import { createStripeAccountSession, createStripeConnect, StripeConnectStatus } from '../api/client';

const CONNECT_JS = 'https://connect-js.stripe.com/v1.0/connect.js';

const APPEARANCE = {
  variables: {
    colorPrimary: '#16a34a',
    colorText: '#1a2e1a',
    colorSecondaryText: '#5a6b5a',
    colorBorder: '#dde5de',
    borderRadius: '8px',
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

let loading: Promise<any> | null = null;

function loadConnectJs(): Promise<any> {
  const ready = () => {
    const sc = (window as any).StripeConnect;
    return sc && typeof sc.init === 'function' ? sc : null;
  };
  if (ready()) return Promise.resolve(ready());
  if (!loading) {
    loading = new Promise((resolve, reject) => {
      const s = document.createElement('script');
      s.src = CONNECT_JS;
      s.async = true;
      s.onload = () => (ready() ? resolve(ready()) : reject(new Error('Stripe’s payments screens did not load.')));
      s.onerror = () => { loading = null; reject(new Error('Could not reach Stripe. Check your connection and try again.')); };
      document.head.appendChild(s);
    });
  }
  return loading;
}

/** One Connect session for the page. Starts only when `start` is true, because
 *  the session call creates the club's payments account if it has none. */
export function useStripeConnect(start: boolean) {
  const [instance, setInstance] = useState<any>(null);
  const [error, setError] = useState('');
  const [testMode, setTestMode] = useState(false);

  useEffect(() => {
    if (!start || instance) return;
    let cancelled = false;
    (async () => {
      try {
        const first = await createStripeAccountSession();
        const sc = await loadConnectJs();
        let firstSecret: string | null = first.clientSecret;
        const fetchClientSecret = async () => {
          if (firstSecret) { const s = firstSecret; firstSecret = null; return s; }
          return (await createStripeAccountSession()).clientSecret;
        };
        sc.optimizedLoading = true;
        const inst = sc.init({ publishableKey: first.publishableKey, fetchClientSecret, appearance: APPEARANCE });
        if (!cancelled) { setInstance(inst); setTestMode(!!first.testMode); }
      } catch (e: any) {
        if (!cancelled) setError(e?.message || 'Could not load the payments screens.');
      }
    })();
    return () => { cancelled = true; };
  }, [start, instance]);

  return { instance, error, testMode };
}

/** One Stripe screen, e.g. "account-onboarding", "payments", "payouts". */
export function ConnectScreen({ instance, name, onExit }: { instance: any; name: string; onExit?: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!instance || !ref.current) return;
    const el: any = document.createElement(`stripe-connect-${name}`);
    if (typeof el.setConnector !== 'function') {
      document.body.appendChild(el);
      document.body.removeChild(el);
    }
    el.setConnector?.(instance.connect);
    if (onExit) el.setOnExitInternalOnly?.(onExit);
    ref.current.appendChild(el);
    return () => { el.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, name]);
  return <div ref={ref} />;
}

/** Settings, Payments for a club-pays account (or none yet). */
export function ClubPaymentsScreens({ status, onChange }: { status: StripeConnectStatus; onChange: () => void }) {
  const ready = !!(status.connected && status.chargesEnabled);
  const [start, setStart] = useState(!!(status.connected && status.clubPaysStripe));
  const [showDetails, setShowDetails] = useState(false);
  const { instance, error, testMode } = useStripeConnect(start);

  if (!start) {
    return (
      <div className="stripe-not-connected">
        <div>
          <p style={{ marginBottom: 8, fontWeight: 500 }}>Set up card payments</p>
          <p className="form-hint" style={{ margin: 0 }}>
            About 5 minutes, without leaving this page. Have the club's bank details ready, and the ID of the person responsible for the club.
          </p>
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setStart(true)} style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
          Set up payments
        </button>
      </div>
    );
  }
  // Stripe's screens unavailable (not configured, or blocked): never a dead end.
  if (error) {
    if (ready) {
      return (
        <div className="stripe-connected">
          <div className="status-badge status-success">Taking payments ✓</div>
          <p className="form-hint" style={{ margin: '8px 0 0' }}>Payments and payouts will show here shortly.</p>
        </div>
      );
    }
    return <HostedFallback />;
  }
  if (!instance) return <p className="form-hint">Loading Stripe…</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {testMode && <div className="status-badge status-warning" style={{ alignSelf: 'flex-start' }}>Test mode: no real money</div>}
      {!ready ? (
        <ConnectScreen instance={instance} name="account-onboarding" onExit={onChange} />
      ) : (
        <>
          <div className="status-badge status-success" style={{ alignSelf: 'flex-start' }}>Taking payments ✓</div>
          <ConnectScreen instance={instance} name="notification-banner" />
          <ConnectScreen instance={instance} name="balances" />
          <ConnectScreen instance={instance} name="payouts" />
          <ConnectScreen instance={instance} name="payments" />
          <button type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => setShowDetails(v => !v)}>
            {showDetails ? 'Hide bank details and documents' : 'Bank details and documents'}
          </button>
          {showDetails && (
            <>
              <ConnectScreen instance={instance} name="account-management" />
              <ConnectScreen instance={instance} name="documents" />
            </>
          )}
        </>
      )}
    </div>
  );
}

/** Stripe's own sign-up page, for when its screens can't load inside ours. */
function HostedFallback() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const go = async () => {
    setBusy(true); setErr('');
    try {
      const { url } = await createStripeConnect();
      if (url) window.location.href = url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Could not start the setup');
      setBusy(false);
    }
  };
  return (
    <div className="stripe-not-connected">
      <div>
        <p style={{ marginBottom: 8, fontWeight: 500 }}>Set up card payments</p>
        <p className="form-hint" style={{ margin: 0 }}>You'll finish the setup on Stripe's site and come straight back here.</p>
        {err && <div className="error-message" style={{ marginTop: 8 }}>{err}</div>}
      </div>
      <button type="button" className="btn btn-primary" onClick={go} disabled={busy} style={{ marginLeft: 'auto', whiteSpace: 'nowrap' }}>
        {busy ? 'Opening Stripe…' : 'Set up on Stripe'}
      </button>
    </div>
  );
}
